import { useEffect, useMemo, useState } from "react";
import XCircleIcon from "@heroicons/react/24/outline/XCircleIcon";
import ChevronRightIcon from "@heroicons/react/24/solid/ChevronRightIcon";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import clsx from "clsx";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import { usePartJSON } from "@hsl/fund-page/hooks";

import { FundExplorerState } from "../../store/types";
import { SimplePartProps, SimplePartSchema } from "../../types";

export default function PromoCard({
    partId,
    audienceId,
    version,
    languageId,
    fundId,
    onFilter,
    style,
    className,
}: SimplePartProps & {
    onFilter?: FundExplorerState["applyFilter"];
    style?: React.CSSProperties;
    className?: string;
}) {
    const { buttonClickEvent } = useYardActionEvents();
    const { data } = usePartJSON(
        true,
        {
            partId: `${partId}`,
            audienceId: `${audienceId}`,
            version,
            languageId: `${languageId}`,
            fundId: `${fundId}`,
        },
        SimplePartSchema,
    );
    const [route] = data?.routes ?? [];
    const initCollapsed = useMemo(() => {
        if (!route) return false;
        const currentParams = new URL(window.location.href).searchParams;
        const routeParams = new URL(route.url).searchParams;
        return routeParams.entries().every(([key, value]) => {
            return currentParams.get(key) === value;
        });
    }, [data?.routes]);
    const [collapsed, setCollapsed] = useState(initCollapsed);
    const [hidden, setHidden] = useState(false);

    const handleClick = (
        href: string,
        e: React.MouseEvent<HTMLElement, MouseEvent>,
    ) => {
        if (e.metaKey || e.ctrlKey) {
            return;
        }
        e.preventDefault();

        const url = new URL(href);

        url.searchParams.entries().forEach(([key, value]) => {
            console.log(key, value);
            onFilter?.({
                level: "fund",
                prop: key,
                type: "primary",
                value: value.split(","),
            });
        });
        setCollapsed(true);
        buttonClickEvent({ name: "Banner", details: "Bonds with bite" });
    };

    useEffect(() => {
        if (initCollapsed) {
            setCollapsed(true);
        }
    }, [initCollapsed]);

    if (!data) return null;
    if (hidden) return null;

    return (
        <div
            className={clsx(
                "group",
                "bg-core-blue text-white",
                "fixed bottom-5 z-50 lg:static",
                "drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)] filter",
                "cursor-pointer",
                collapsed && "hidden lg:block",
                className,
            )}
            style={style}
            onClick={handleClick.bind(null, route?.url ?? "")}
        >
            <div
                className={clsx(
                    "relative grid",
                    !collapsed && "grid-cols-3 lg:grid-cols-2",
                    collapsed && "grid-cols-3",
                )}
            >
                <img
                    src={data.image}
                    className={clsx(
                        "block h-full object-cover",
                        collapsed && "col-span-1",
                    )}
                />
                <div
                    className={clsx(
                        "col-span-2 flex flex-col px-5 py-5 lg:col-span-1 lg:px-10",
                        collapsed && "lg:col-span-2",
                    )}
                >
                    {data.title && (
                        <h3 className="font-serif text-lg font-bold lg:text-2xl">
                            {data.title}
                        </h3>
                    )}
                    {data.text && (
                        <div
                            className="text-xs lg:text-sm"
                            dangerouslySetInnerHTML={{ __html: data.text }}
                        ></div>
                    )}
                    {route && !collapsed && (
                        <div className="mt-4 lg:mt-auto">
                            <a
                                href={route.url}
                                onClick={handleClick.bind(null, route.url)}
                                className="flex items-center justify-start text-xs font-normal group-hover:underline lg:text-sm lg:font-medium"
                            >
                                {route.link_text}{" "}
                                <ChevronRightIcon
                                    color="white"
                                    width={18}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 lg:static lg:translate-y-0"
                                />
                            </a>
                        </div>
                    )}
                </div>
                <XCircleIcon
                    width={24}
                    color="white"
                    className="absolute right-4 top-4 z-30 hidden cursor-pointer lg:block"
                    onClick={(e) => {
                        e.stopPropagation();
                        setHidden(true);
                        buttonClickEvent({
                            name: "Banner",
                            details: "Bonds with bite - close",
                        });
                    }}
                />
                <a
                    className="absolute right-0 top-0 -translate-y-full border-[1px] border-b-0 border-[#333] bg-white lg:hidden"
                    onClick={(e) => {
                        e.stopPropagation();
                        setHidden(true);
                        buttonClickEvent({
                            name: "Banner",
                            details: "Bonds with bite - close",
                        });
                    }}
                >
                    <XMarkIcon color={"#333"} width={24} />
                </a>
            </div>
        </div>
    );
}
